/**
 * App 그룹 브릿지 React Query Options
 */

import { queryOptions } from '@tanstack/react-query'

import type { ActionRequest } from '@/lib/bridge/types'

import * as methods from './methods'

export const appQueryKeys = {
  all: ['bridge', 'app'] as const,
  getConfig: () => [...appQueryKeys.all, 'getConfig'] as const,
  getColor: () => [...appQueryKeys.all, 'getColor'] as const,
  getValue: (params: ActionRequest<'App', 'getValue'>) => [...appQueryKeys.all, 'getValue', params] as const,
  isPassedTargetPackages: (params: ActionRequest<'App', 'isPassedTargetPackages'>) =>
    [...appQueryKeys.all, 'isPassedTargetPackages', params] as const,
  getRequestId: () => [...appQueryKeys.all, 'getRequestId'] as const,
  isPackageInstalled: (params: ActionRequest<'App', 'isPackageInstalled'>) =>
    [...appQueryKeys.all, 'isPackageInstalled', params] as const,
  insertCpiAd: (params: ActionRequest<'App', 'insertCpiAd'>) => [...appQueryKeys.all, 'insertCpiAd', params] as const,
  isMarketEnabled: () => [...appQueryKeys.all, 'isMarketEnabled'] as const
}

export const appQueryOptions = {
  /**
   * 앱 설정 조회
   * 세션 동안 변하지 않으므로 무한대 staleTime 설정
   */
  getConfig: () =>
    queryOptions({
      queryKey: appQueryKeys.getConfig(),
      queryFn: () => methods.getConfig(),
      staleTime: Infinity, // 세션 동안 변하지 않음
      gcTime: 1000 * 60 * 60 // 1시간 동안 캐시 유지
    }),

  /**
   * 앱 색상 조회
   * 세션 동안 변하지 않으므로 무한대 staleTime 설정
   */
  getColor: () =>
    queryOptions({
      queryKey: appQueryKeys.getColor(),
      queryFn: () => methods.getColor(),
      staleTime: Infinity, // 세션 동안 변하지 않음
      gcTime: 1000 * 60 * 60 // 1시간 동안 캐시 유지
    }),

  /**
   * 키-값 조회
   * 값이 변경될 수 있으므로 짧은 staleTime 설정
   */
  getValue: (params: ActionRequest<'App', 'getValue'>) =>
    queryOptions({
      queryKey: appQueryKeys.getValue(params),
      queryFn: () => methods.getValue(params),
      staleTime: 1000 * 60 * 5, // 5분 (값이 변경될 수 있음)
      gcTime: 1000 * 60 * 10 // 10분 동안 캐시 유지
    }),

  /**
   * 타겟 패키지 설치 여부 확인
   * 같은 패키지는 계속 같은 결과를 반환하므로 무한대 staleTime 설정
   */
  isPassedTargetPackages: (params: ActionRequest<'App', 'isPassedTargetPackages'>) =>
    queryOptions({
      queryKey: appQueryKeys.isPassedTargetPackages(params),
      queryFn: () => methods.isPassedTargetPackages(params),
      staleTime: Infinity, // 같은 패키지는 계속 같은 결과
      gcTime: 1000 * 60 * 60 // 1시간 동안 캐시 유지
    }),

  /**
   * 요청 ID 조회
   * 세션 동안 변하지 않으므로 무한대 staleTime 설정
   */
  getRequestId: () =>
    queryOptions({
      queryKey: appQueryKeys.getRequestId(),
      queryFn: () => methods.getRequestId(),
      staleTime: Infinity, // 세션 동안 변하지 않음
      gcTime: 1000 * 60 * 60 // 1시간 동안 캐시 유지
    }),

  /**
   * 패키지 설치 여부 확인
   * 같은 패키지는 계속 같은 결과를 반환하므로 무한대 staleTime 설정
   */
  isPackageInstalled: (params: ActionRequest<'App', 'isPackageInstalled'>) =>
    queryOptions({
      queryKey: appQueryKeys.isPackageInstalled(params),
      queryFn: () => methods.isPackageInstalled(params),
      staleTime: Infinity, // 같은 패키지는 계속 같은 결과
      gcTime: 1000 * 60 * 60 // 1시간 동안 캐시 유지
    }),

  /**
   * CPI 광고 추적 정보 저장
   * CPI 광고 참여 시 설치 추적 정보를 저장하여 추후 앱 설치 시 리워드를 지급할 수 있도록 하기 위한 설정
   */
  insertCpiAd: (params: ActionRequest<'App', 'insertCpiAd'>) =>
    queryOptions({
      queryKey: appQueryKeys.insertCpiAd(params),
      queryFn: () => methods.insertCpiAd(params),
      staleTime: Infinity, // 세션 동안 변하지 않음
      gcTime: 1000 * 60 * 60 // 1시간 동안 캐시 유지
    }),

  /**
   * 마켓 활성화 여부 확인
   * 세션 동안 변하지 않으므로 무한대 staleTime 설정
   */
  isMarketEnabled: () =>
    queryOptions({
      queryKey: appQueryKeys.isMarketEnabled(),
      queryFn: () => methods.isMarketEnabled(),
      staleTime: Infinity, // 세션 동안 변하지 않음
      gcTime: 1000 * 60 * 60 // 1시간 동안 캐시 유지
    })
}
